// =============================================================================
// Platform Users Page — Read-only list of all platform users (Sprint 1B)
// =============================================================================

import { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardHeader,
  Button,
  Badge,
  Input,
  Spinner,
} from '@tkpod/ui';
import { api } from '@/lib/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PlatformUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  avatar: string | null;
  status: 'ACTIVE' | 'SUSPENDED' | 'PENDING_VERIFICATION' | 'DELETED';
  emailVerified: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  organizationCount: number;
  isAdmin: boolean;
}

interface PlatformUsersResponse {
  data: PlatformUser[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

type StatusFilter = '' | 'ACTIVE' | 'SUSPENDED' | 'PENDING_VERIFICATION' | 'DELETED';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PAGE_LIMIT = 20;

const STATUS_CONFIG: Record<
  PlatformUser['status'],
  { label: string; variant: 'success' | 'danger' | 'warning' | 'default' }
> = {
  ACTIVE: { label: 'Active', variant: 'success' },
  SUSPENDED: { label: 'Suspended', variant: 'danger' },
  PENDING_VERIFICATION: { label: 'Pending', variant: 'warning' },
  DELETED: { label: 'Deleted', variant: 'default' },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function displayName(user: PlatformUser): string {
  const parts = [user.firstName, user.lastName].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : user.email;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PlatformUsersPage() {
  // -- Local state ----------------------------------------------------------
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');
  const [page, setPage] = useState(1);

  // Reset page when filters change
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.target.value);
      setPage(1);
    },
    [],
  );

  const handleStatusChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setStatusFilter(e.target.value as StatusFilter);
      setPage(1);
    },
    [],
  );

  const clearFilters = useCallback(() => {
    setSearch('');
    setStatusFilter('');
    setPage(1);
  }, []);

  // -- Query ----------------------------------------------------------------
  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(PAGE_LIMIT));
    if (search.trim()) params.set('search', search.trim());
    if (statusFilter) params.set('status', statusFilter);
    return params.toString();
  }, [page, search, statusFilter]);

  const usersQuery = useQuery<PlatformUsersResponse>({
    queryKey: ['platform-users', page, search, statusFilter],
    queryFn: () =>
      api.get<PlatformUsersResponse>(
        `/api/v1/admin/platform-users?${queryParams}`,
      ),
  });

  const users = usersQuery.data?.data ?? [];
  const pagination = usersQuery.data?.pagination;
  const hasFilters = !!(search || statusFilter);

  // -- Loading state --------------------------------------------------------
  if (usersQuery.isLoading && !usersQuery.data) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="lg" label="Loading platform users..." />
      </div>
    );
  }

  // -- Error state ----------------------------------------------------------
  if (usersQuery.isError) {
    return (
      <Card>
        <div className="flex flex-col items-center justify-center gap-4 py-16">
          <p className="text-sm text-red-400">
            Failed to load platform users:{' '}
            {usersQuery.error?.message ?? 'Unknown error'}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void usersQuery.refetch()}
          >
            Retry
          </Button>
        </div>
      </Card>
    );
  }

  // -- Render ---------------------------------------------------------------
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Platform Users"
          description="View all registered platform users across the system. Read-only in this sprint."
        />

        {/* Filters row */}
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <div className="w-full max-w-xs">
            <Input
              placeholder="Search by name or email..."
              value={search}
              onChange={handleSearchChange}
            />
          </div>

          <select
            value={statusFilter}
            onChange={handleStatusChange}
            className="rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500"
          >
            <option value="">All Statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="SUSPENDED">Suspended</option>
            <option value="PENDING_VERIFICATION">Pending Verification</option>
            <option value="DELETED">Deleted</option>
          </select>

          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Clear Filters
            </Button>
          )}

          {/* Refetching indicator */}
          {usersQuery.isFetching && usersQuery.data && (
            <Spinner size="sm" />
          )}
        </div>

        {/* Users table */}
        <div className="mt-4 overflow-x-auto rounded-lg border border-border">
          {users.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-sm text-text-tertiary">
              {hasFilters
                ? 'No users match your filters.'
                : 'No platform users found.'}
            </div>
          ) : (
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-surface-secondary">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Email Verified
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Organizations
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Admin
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Last Login
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-surface">
                {users.map((user) => {
                  const statusCfg = STATUS_CONFIG[user.status];

                  return (
                    <tr
                      key={user.id}
                      className="transition-colors hover:bg-surface-secondary"
                    >
                      {/* Name */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Link
                          to={`/platform-users/${user.id}`}
                          className="text-sm font-medium text-brand-400 hover:text-brand-300 hover:underline"
                        >
                          {displayName(user)}
                        </Link>
                      </td>

                      {/* Email */}
                      <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">
                        {user.email}
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Badge variant={statusCfg.variant} size="sm" dot>
                          {statusCfg.label}
                        </Badge>
                      </td>

                      {/* Email Verified */}
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        {user.emailVerified ? (
                          <span
                            className="inline-flex text-green-400"
                            title="Verified"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                              className="h-5 w-5"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </span>
                        ) : (
                          <span
                            className="inline-flex text-red-400"
                            title="Not verified"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                              className="h-5 w-5"
                            >
                              <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                            </svg>
                          </span>
                        )}
                      </td>

                      {/* Organizations */}
                      <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap text-center">
                        {user.organizationCount}
                      </td>

                      {/* Admin */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        {user.isAdmin ? (
                          <Badge variant="primary" size="sm">
                            Yes
                          </Badge>
                        ) : (
                          <span className="text-sm text-text-tertiary">No</span>
                        )}
                      </td>

                      {/* Last Login */}
                      <td className="px-4 py-3 text-sm text-text-tertiary whitespace-nowrap">
                        {user.lastLoginAt
                          ? formatDate(user.lastLoginAt)
                          : 'Never'}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Link to={`/platform-users/${user.id}`}>
                          <Button variant="ghost" size="sm">
                            View
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {pagination && pagination.totalPages > 0 && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-text-tertiary">
              Showing{' '}
              {users.length === 0
                ? 0
                : (pagination.page - 1) * pagination.limit + 1}
              –
              {Math.min(
                pagination.page * pagination.limit,
                pagination.total,
              )}{' '}
              of {pagination.total} users
            </p>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>

              {/* Page number indicators */}
              <PageNumbers
                current={pagination.page}
                total={pagination.totalPages}
                onPageChange={setPage}
              />

              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page >= pagination.totalPages}
                onClick={() =>
                  setPage((p) => Math.min(pagination.totalPages, p + 1))
                }
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * Compact page number indicators with ellipsis for large page counts.
 */
function PageNumbers({
  current,
  total,
  onPageChange,
}: {
  current: number;
  total: number;
  onPageChange: (page: number) => void;
}) {
  if (total <= 1) return null;

  // Build visible page numbers: always show first, last, current, and neighbors
  const pages: (number | 'ellipsis')[] = [];
  const addPage = (n: number) => {
    if (n >= 1 && n <= total && !pages.includes(n)) {
      pages.push(n);
    }
  };

  addPage(1);
  addPage(current - 1);
  addPage(current);
  addPage(current + 1);
  addPage(total);

  // Sort numeric pages and insert ellipsis where there are gaps
  const numericPages = (pages.filter((p) => typeof p === 'number') as number[]).sort(
    (a, b) => a - b,
  );

  const result: (number | 'ellipsis')[] = [];
  for (let i = 0; i < numericPages.length; i++) {
    if (i > 0 && numericPages[i] - numericPages[i - 1] > 1) {
      result.push('ellipsis');
    }
    result.push(numericPages[i]);
  }

  return (
    <div className="flex items-center gap-1">
      {result.map((item, idx) =>
        item === 'ellipsis' ? (
          <span
            key={`ellipsis-${idx}`}
            className="px-1 text-xs text-text-tertiary"
          >
            ...
          </span>
        ) : (
          <button
            key={item}
            type="button"
            onClick={() => onPageChange(item)}
            className={`flex h-8 min-w-[2rem] items-center justify-center rounded-md px-2 text-xs font-medium transition-colors ${
              item === current
                ? 'bg-brand-600 text-white'
                : 'text-text-secondary hover:bg-surface-secondary hover:text-text-primary'
            }`}
          >
            {item}
          </button>
        ),
      )}
    </div>
  );
}
